import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FilterPanel = ({ onFilter, onClear }) => {
  const [filters, setFilters] = useState({
    vendor: '',
    category: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    amountMin: '',
    amountMax: ''
  });

  const vendorOptions = [
    { value: '', label: 'Select vendor' },
    { value: 'office depot', label: 'Office Depot' },
    { value: 'adobe systems', label: 'Adobe Systems' },
    { value: 'electric company', label: 'Electric Company' },
    { value: 'internet provider', label: 'Internet Provider' },
    { value: 'property management', label: 'Property Management Co' },
    { value: 'slack technologies', label: 'Slack Technologies' },
    { value: 'insurance corp', label: 'Insurance Corp' },
    { value: 'clean pro services', label: 'Clean Pro Services' }
  ];

  const categoryOptions = [
    { value: '', label: 'Select category' },
    { value: 'office supplies', label: 'Office Supplies' },
    { value: 'software', label: 'Software' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'communications', label: 'Communications' },
    { value: 'services', label: 'Services' },
    { value: 'office expenses', label: 'Office Expenses' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'travel', label: 'Travel' },
    { value: 'meals & entertainment', label: 'Meals & Entertainment' }
  ];

  const statusOptions = [
    { value: '', label: 'Select status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'completed', label: 'Completed' },
    { value: 'processing', label: 'Processing' },
    { value: 'failed', label: 'Failed' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'paused', label: 'Paused' }
  ];

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    onFilter?.(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      vendor: '',
      category: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      amountMin: '',
      amountMax: ''
    };
    setFilters(clearedFilters);
    onClear?.();
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Filter" size={20} />
          <span>Filters</span>
        </h3>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            <Icon name="X" size={16} />
            Clear All
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Vendor Filter */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Vendor
          </label>
          <Select
            options={vendorOptions}
            value={filters?.vendor}
            onChange={(value) => handleFilterChange('vendor', value)}
            placeholder="Select vendor"
          />
        </div>

        {/* Category Filter */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Category
          </label>
          <Select
            options={categoryOptions}
            value={filters?.category}
            onChange={(value) => handleFilterChange('category', value)}
            placeholder="Select category"
          />
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Status
          </label>
          <Select
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
            placeholder="Select status"
          />
        </div>

        {/* Quick Amount Filter */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Amount Range
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              placeholder="0"
              value={filters?.amountMin}
              onChange={(e) => handleFilterChange('amountMin', e?.target?.value)}
              className="text-sm"
            />
            <Input
              type="number"
              placeholder="10000"
              value={filters?.amountMax}
              onChange={(e) => handleFilterChange('amountMax', e?.target?.value)}
              className="text-sm"
            />
          </div>
        </div>
      </div>

      {/* Date Range Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Date From */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Date From
          </label>
          <Input
            type="date"
            value={filters?.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
          />
        </div>

        {/* Date To */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Date To
          </label>
          <Input
            type="date"
            value={filters?.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
          />
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-6 pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground mb-3">Active Filters:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(filters)?.map(([key, value]) => {
              if (!value) return null;
              
              let displayValue = value;
              let displayKey = key;
              
              // Format display names
              switch (key) {
                case 'dateFrom':
                  displayKey = 'From';
                  break;
                case 'dateTo':
                  displayKey = 'To';
                  break;
                case 'amountMin':
                  displayKey = 'Min';
                  displayValue = `$${value}`;
                  break;
                case 'amountMax':
                  displayKey = 'Max';
                  displayValue = `$${value}`;
                  break;
                default:
                  displayKey = key?.charAt(0)?.toUpperCase() + key?.slice(1);
              }
              
              return (
                <span
                  key={key}
                  className="inline-flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary text-sm rounded-full"
                >
                  <span>{displayKey}: {displayValue}</span>
                  <button
                    onClick={() => handleFilterChange(key, '')}
                    className="hover:bg-primary/20 rounded-full p-0.5 ml-1"
                    title="Remove filter"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;